/**
 * useMobileBackHandler — Fängt den Browser-Back-Button auf Mobile ab
 * 
 * Problem: Wenn ein Dialog/Modal geöffnet ist und der User den
 * Hardware-Back-Button drückt, navigiert der Browser zur vorherigen
 * Route statt den Dialog zu schließen.
 * 
 * Lösung: Wenn ein Dialog geöffnet wird, wird ein History-Eintrag
 * hinzugefügt. Beim Back-Button wird der Dialog geschlossen statt
 * die Seite zu wechseln.
 */
import { useEffect, useRef } from 'react';

/**
 * Hook der den Browser-Back-Button abfängt wenn ein Dialog/Modal offen ist.
 * 
 * @param isOpen - Ob der Dialog/Modal gerade geöffnet ist
 * @param onClose - Callback zum Schließen des Dialogs
 */
export function useMobileBackHandler(isOpen: boolean, onClose: () => void) {
  const isOpenRef = useRef(isOpen);
  const onCloseRef = useRef(onClose);
  const historyPushed = useRef(false);

  // Refs aktuell halten
  useEffect(() => {
    isOpenRef.current = isOpen;
    onCloseRef.current = onClose;
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && !historyPushed.current) {
      // Dialog wurde geöffnet → History-Eintrag hinzufügen
      window.history.pushState({ dialogOpen: true }, '');
      historyPushed.current = true;
    }

    if (!isOpen && historyPushed.current) {
      // Dialog wurde programmatisch geschlossen (nicht durch Back-Button)
      // → History-Eintrag entfernen damit die Navigation sauber bleibt
      historyPushed.current = false;
      // Nur zurückgehen wenn der aktuelle State unser Dialog-State ist
      if (window.history.state?.dialogOpen) {
        window.history.back();
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isOpenRef.current && historyPushed.current) {
        // Back-Button gedrückt während Dialog offen → Dialog schließen
        event.preventDefault();
        historyPushed.current = false;
        onCloseRef.current();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      // Cleanup: Falls der Hook unmounted wird während Dialog offen ist
      if (historyPushed.current) {
        historyPushed.current = false;
      }
    };
  }, []);
}

/**
 * Hook der den Browser-Back-Button abfängt für Mobile-Detail-Ansichten.
 * Wenn der User in einer Detail-Ansicht ist und Back drückt,
 * wird zur Liste zurückgekehrt statt die Seite zu verlassen.
 * 
 * @param isDetailView - Ob gerade eine Detail-Ansicht angezeigt wird
 * @param onBack - Callback zum Zurückkehren zur Liste
 */
export function useMobileDetailBackHandler(isDetailView: boolean, onBack: () => void) {
  const isDetailRef = useRef(isDetailView);
  const onBackRef = useRef(onBack);
  const historyPushed = useRef(false);

  useEffect(() => {
    isDetailRef.current = isDetailView;
    onBackRef.current = onBack;
  }, [isDetailView, onBack]);

  useEffect(() => {
    if (isDetailView && !historyPushed.current) {
      window.history.pushState({ detailView: true }, '');
      historyPushed.current = true;
    }

    if (!isDetailView && historyPushed.current) {
      historyPushed.current = false;
      if (window.history.state?.detailView) {
        window.history.back();
      }
    }
  }, [isDetailView]);

  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isDetailRef.current && historyPushed.current) {
        event.preventDefault();
        historyPushed.current = false;
        onBackRef.current();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      if (historyPushed.current) {
        historyPushed.current = false;
      }
    };
  }, []);
}
